<template>
    <form class="form podcast-episode" @submit.prevent="saveEpisode">
        <Spinner v-if="loading" />
        <h1 class="podcast-episode__heading">
            {{ $t('podcasts.episodes.detail') }}
        </h1>
        <div class="podcast-episode__half">
            <div class="podcast-episode__col">
                <div class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.episodes.type') }}
                        <i class="form__required">*</i>
                    </label>
                    <RadioGroup
                        v-model="episode.episode_type.name"
                        name="type"
                        label="label"
                        prop="name"
                        style="margin-top: 10px;"
                        :options="types"
                    />
                    <p class="form__notice">
                        {{ $t('podcasts.episodes.typeNotice') }}
                    </p>
                </div>
            </div>
            <div v-if="episode.id" class="podcast-episode__col">
                <div class="form-group">
                    <label class="form-group__label">
                        {{ $t('podcasts.episodes.builds') }}
                        <i class="form__required">*</i>
                    </label>
                    <div class="podcast-episode__builds">
                        <div
                            v-for="(buildType, idx) in episodeBuildTypes"
                            :key="idx"
                            v-tooltip="
                                episodeHasBuild(buildType)
                                    ? $t('posdasts.episodes.hasBuild')
                                    : $t('podcasts.episodes.nobuild')
                            "
                            class="podcast-episode__build"
                            :class="{
                                'podcast-episode__build--nobuild': !episodeHasBuild(buildType)
                            }"
                        >
                            {{ buildType }}
                        </div>
                    </div>
                    <p class="form__notice">
                        {{ $t('podcasts.episodes.buildsNotice') }}
                    </p>
                </div>
            </div>
        </div>
        <div class="form-group">
            <label class="form-group__label">
                {{ $t('podcasts.detail.descr') }}
                <i class="form__required">*</i>
            </label>
            <textarea
                v-model="episode.description"
                class="form-group__textarea"
                :placeholder="$t('podcasts.detail.descr')"
                style="display: block; margin-top: 10px;"
                required="true"
            />
        </div>
        <div class="form-group">
            <label class="form-group__label">
                {{ $t('podcasts.episodes.book') }}
                <i class="form__required">*</i>
            </label>
            <PodcastBookSelector v-model="episode.book.id" />
        </div>
        <div class="podcast-episode__actions">
            <button
                :disabled="loading"
                style="margin-right: 1em;"
                class="button button--big"
                type="sumbit"
            >
                {{ $t('misc.save') }}
            </button>
            <button
                :disabled="loading"
                style="margin-right: 1em;"
                class="button button--big button--blank"
                type="button"
                @click="cancel"
            >
                {{ $t('misc.cancel') }}
            </button>
            <button
                v-if="episode.id"
                :disabled="loading"
                style="margin-right: 1em;"
                class="link link--red"
                type="button"
                @click="deleteEpisode"
            >
                {{ $t('podcasts.episodes.delete') }}
            </button>
            <ErrorList :error="error" />
        </div>
    </form>
</template>

<script>
import { RadioGroup, ErrorList } from '@speechki-frontend/shared/ui/forms';
import PodcastBookSelector from '../PodcastBookSelector/PodcastBookSelector';

const EPISODE_TYPE_MAPPING = {
    full: ['mp3'],
    sample: ['sample'],
    all: ['sample', 'mp3']
};

export default {
    name: 'PodcastEpisodeForm',
    components: {
        RadioGroup,
        PodcastBookSelector,
        ErrorList
    },
    props: {
        id: {
            type: [Number, String],
            default: undefined
        },
        podcast: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            episode: {
                id: '',
                description: '',
                episode_type: {
                    name: '',
                    label: ''
                },
                book: {
                    id: '',
                    isbnx: '',
                    name: ''
                }
            },
            loading: true,
            types: [],
            error: null
        };
    },
    computed: {
        episodeBuildTypes() {
            if (!this.episode.id) return [];

            return EPISODE_TYPE_MAPPING[this.episode.episode_type.name];
        }
    },
    watch: {
        id: {
            async handler() {
                if (!this.id) return;

                this.loading = true;
                await this.fetchEpisode();
                this.loading = false;
            }
        }
    },
    async created() {
        this.loading = true;
        const promises = [this.fetchTypes()];

        if (this.id) {
            promises.push(this.fetchEpisode());
        }

        await Promise.all[promises];
        this.loading = false;
    },
    methods: {
        async fetchEpisode() {
            try {
                this.episode = await this.$http.get(
                    `/api/v1/podcast/episodes_settings/${this.id}/`
                );
            } catch (e) {
                console.log(e);
            }
        },
        async fetchTypes() {
            try {
                this.types = await this.$http.get(
                    `/api/v1/podcast/episodes_settings/episode_types/`
                );
            } catch (e) {
                console.log(e);
            }
        },
        async saveEpisode() {
            const method = this.id ? 'patch' : 'post';

            try {
                this.error = null;
                this.loading = true;
                const data = {
                    book: this.episode.book.id,
                    description: this.episode.description,
                    podcast: this.podcast,
                    episode_type: this.episode.episode_type.name
                };

                await this.$http[method](
                    `/api/v1/podcast/episodes_settings/${this.id ? `${this.id}/` : ''}`,
                    data
                );

                this.$emit('update');

                this.loading = false;
            } catch (e) {
                this.error = e;
                this.loading = false;
                console.log(e);
            }
        },
        cancel() {
            this.$emit('update');
        },
        async deleteEpisode() {
            try {
                this.error = null;
                this.loading = true;

                await this.$http.delete(`/api/v1/podcast/episodes_settings/${this.episode.id}/`);

                this.loading = false;
                this.$emit('update');
            } catch (e) {
                this.loading = false;
                this.error = e;
            }
        },
        episodeHasBuild(type) {
            return this.episode.builds.findIndex(build => build.build_type === type) > -1;
        }
    }
};
</script>
